import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useSelectedBoat } from '@/entities/boat';
import { useIsPlant } from '@/entities/user';
import PlantSelectSvg from '@/shared/assets/icons/header/select-plant.svg';
import SelectSvg from '@/shared/assets/icons/header/select-ship.svg';
import { useNamespace } from '@/shared/lib/hooks/use-namespace.hook';
import { SearchableSelect } from '@/shared/ui/searchable-select';

import { SELECT_DEFAULT_VALUE } from '../../model/constants/select-default.constant';
import { selectBoatForUser } from '../../model/selectors/select-boat-input';
import { useUpdateBoatId } from '../../model/utils/hooks/use-update-boat-id.hook';
import { SelectBoatProps } from './select-boat.types';

const SelectBoatComponent = ({
	redirect = true,
	withoutAll = false,
	onSelect,
	searchMode = false,
}: SelectBoatProps) => {
	const namespace = useNamespace('common');
	const { t } = useTranslation(namespace as ['plant', 'common'] | 'common');
	const isPlant = useIsPlant();
	const { items, length } = useSelector((state) => selectBoatForUser(state, withoutAll));
	const selectedBoat = useSelectedBoat();
	const onChangeBoat = useUpdateBoatId({ redirect });
	const handleChangeBoat = useCallback(
		(id: string) => {
			onChangeBoat(id);
			onSelect?.();
		},
		[onChangeBoat, onSelect],
	);

	if (!length) {
		return null;
	}

	return (
		<SearchableSelect
			options={items}
			value={selectedBoat?.id ?? SELECT_DEFAULT_VALUE}
			onChange={handleChangeBoat}
			Adornment={isPlant ? PlantSelectSvg : SelectSvg}
			placeholder={t('select.boat.empty')}
			displaySelectedValue={!searchMode}
			autoFocus={searchMode}
		/>
	);
};

export default memo(SelectBoatComponent);
